import { useEffect, useState } from "react";
import {
  Bot,
  CheckCircle2,
  Circle,
  Loader2,
  MinusCircle,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import { Button, FormField, Modal, TextInput } from "../../components/ui";
import {
  LIMITE_DIAS_POR_INTEGRACAO,
  diasDoPeriodo,
  formatarDataBr,
  hojeLocal,
  validarPeriodo,
} from "./integracaoSisodontoUtils";

const ROTULOS_DIA = {
  aguardando: "Aguardando",
  executando: "Em andamento",
  criado: "Concluído",
  ignorado: "Ignorado",
  erro: "Erro",
  nao_executado: "Não executado",
};

const IconeDoDia = ({ status }) => {
  switch (status) {
    case "executando":
      return (
        <Loader2
          size={18}
          className="animate-spin text-blue-600"
          aria-hidden="true"
        />
      );
    case "criado":
      return (
        <CheckCircle2 size={18} className="text-green-600" aria-hidden="true" />
      );
    case "ignorado":
      return (
        <MinusCircle size={18} className="text-amber-600" aria-hidden="true" />
      );
    case "erro":
      return <XCircle size={18} className="text-red-600" aria-hidden="true" />;
    default:
      return <Circle size={18} className="text-gray-300" aria-hidden="true" />;
  }
};

const InstrucoesSessaoExpirada = () => (
  <div
    role="alert"
    className="mt-4 flex gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
  >
    <ShieldAlert size={18} className="mt-0.5 shrink-0" aria-hidden="true" />
    <div className="space-y-2">
      <p className="font-medium">Sessão do Sisodonto expirada</p>
      <p>Renove o login no PC do robô:</p>
      <ol className="list-decimal space-y-1 pl-5">
        <li>Feche o robô (a janela “Ligar-Robo”).</li>
        <li>
          Abra <strong>Login-Sisodonto</strong> e entre no Sisodonto na janela
          que abrir.
        </li>
        <li>Ligue o robô de novo e refaça a integração.</li>
      </ol>
      <p className="text-xs">
        A senha do Sisodonto é digitada só no navegador desse PC e nunca passa
        pelo PCOE.
      </p>
    </div>
  </div>
);

const ResumoFinal = ({ execucao }) => {
  const criados = execucao.dias.filter((dia) => dia.status === "criado").length;
  const ignorados = execucao.dias.filter(
    (dia) => dia.status === "ignorado",
  ).length;
  const naoExecutados = execucao.dias.filter(
    (dia) => dia.status === "nao_executado",
  ).length;

  const partes = [
    `${criados} caixa(s) criado(s)`,
    ignorados ? `${ignorados} dia(s) ignorado(s) por já terem caixa` : null,
    naoExecutados ? `${naoExecutados} dia(s) não executado(s)` : null,
  ].filter(Boolean);

  let titulo = "Integração concluída";
  let classes = "border-green-200 bg-green-50 text-green-900";
  if (execucao.motivoFim === "interrompido") {
    titulo = "Integração interrompida";
    classes = "border-amber-300 bg-amber-50 text-amber-900";
  } else if (execucao.motivoFim === "erro") {
    titulo = "Integração interrompida por um erro";
    classes = "border-red-200 bg-red-50 text-red-900";
  }

  return (
    <div
      role="status"
      data-testid="resumo-integracao"
      className={`mt-4 rounded-lg border p-3 text-sm ${classes}`}
    >
      <p className="font-medium">{titulo}</p>
      <p>{partes.join(", ")}.</p>
      {execucao.motivoFim === "erro" && !execucao.sessaoExpirada && (
        <p className="mt-1">{execucao.mensagemErro}</p>
      )}
    </div>
  );
};

/**
 * Modal da integração com o Sisodonto: escolhe o período, mostra o progresso
 * de cada dia e o resultado. O fluxo (um pedido por dia) fica em
 * useIntegracaoSisodonto; aqui só há apresentação.
 */
const IntegracaoSisodontoModal = ({
  open,
  onClose,
  agente,
  execucao,
  dataPadrao,
  onIniciar,
  onInterromper,
  onNovaIntegracao,
}) => {
  const [dataInicial, setDataInicial] = useState(dataPadrao);
  const [dataFinal, setDataFinal] = useState(dataPadrao);

  useEffect(() => {
    if (open) {
      setDataInicial(dataPadrao);
      setDataFinal(dataPadrao);
    }
    // só reinicia ao abrir; mudar a data do filtro com o modal aberto não mexe nos campos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const executando = execucao?.estado === "executando";
  const online = Boolean(agente.online);
  const erroPeriodo = validarPeriodo(dataInicial, dataFinal);
  const quantidadeDias = erroPeriodo
    ? 0
    : diasDoPeriodo(dataInicial, dataFinal).length;

  const fechar = () => {
    if (!executando) {
      onClose();
    }
  };

  const total = execucao?.dias.length || 0;
  const concluidos = execucao
    ? execucao.dias.filter((dia) =>
        ["criado", "ignorado", "erro", "nao_executado"].includes(dia.status),
      ).length
    : 0;
  const diaAtual = execucao?.dias.findIndex(
    (dia) => dia.status === "executando",
  );

  return (
    <Modal
      open={open}
      onClose={fechar}
      title="Integração com o Sisodonto"
      description={
        execucao
          ? undefined
          : "Escolha o período. O robô busca o Pix automático de cada dia, um de cada vez."
      }
      className="max-w-xl"
    >
      {!execucao && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Data inicial" htmlFor="integracao-data-inicial">
              <TextInput
                id="integracao-data-inicial"
                type="date"
                max={hojeLocal()}
                value={dataInicial}
                onChange={(evento) => setDataInicial(evento.target.value)}
              />
            </FormField>
            <FormField label="Data final" htmlFor="integracao-data-final">
              <TextInput
                id="integracao-data-final"
                type="date"
                max={hojeLocal()}
                value={dataFinal}
                onChange={(evento) => setDataFinal(evento.target.value)}
              />
            </FormField>
          </div>

          {erroPeriodo ? (
            <p role="alert" className="mt-3 text-sm font-medium text-red-600">
              {erroPeriodo}
            </p>
          ) : (
            <p className="mt-3 text-sm text-gray-600">
              {quantidadeDias} dia(s) a integrar (máximo de{" "}
              {LIMITE_DIAS_POR_INTEGRACAO}). Dias que já têm caixa lançado são
              ignorados.
            </p>
          )}

          <p
            data-testid="estado-robo-modal"
            className={`mt-3 text-sm ${online ? "text-green-700" : "text-gray-600"}`}
          >
            {online
              ? "Robô online."
              : "Robô offline. Abra o robô no seu PC para integrar."}
          </p>

          <div className="mt-5 flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              icon={Bot}
              disabled={Boolean(erroPeriodo) || !online}
              onClick={() => onIniciar(dataInicial, dataFinal)}
            >
              Iniciar integração
            </Button>
          </div>
        </>
      )}

      {execucao && (
        <>
          <div className="mb-3">
            <div className="mb-1 flex justify-between text-sm text-gray-700">
              <span data-testid="progresso-texto">
                {executando && diaAtual >= 0
                  ? `Dia ${diaAtual + 1} de ${total}`
                  : `${concluidos} de ${total} dia(s)`}
              </span>
              <span>{Math.round((concluidos / total) * 100)}%</span>
            </div>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={total}
              aria-valuenow={concluidos}
              className="h-2 overflow-hidden rounded-full bg-gray-200"
            >
              <div
                className="h-full bg-blue-600 transition-all"
                style={{ width: `${(concluidos / total) * 100}%` }}
              />
            </div>
          </div>

          <ul
            aria-label="Progresso por dia"
            className="max-h-72 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200"
          >
            {execucao.dias.map((dia) => (
              <li
                key={dia.data}
                data-testid={`dia-${dia.data}`}
                data-status={dia.status}
                className="flex items-center gap-3 px-3 py-2 text-sm"
              >
                <IconeDoDia status={dia.status} />
                <span className="w-24 shrink-0 font-medium text-gray-900">
                  {formatarDataBr(dia.data)}
                </span>
                <span className="min-w-0 flex-1 text-gray-600">
                  <span className="sr-only">{ROTULOS_DIA[dia.status]}: </span>
                  {dia.status === "executando"
                    ? dia.etapa
                    : dia.mensagem || ROTULOS_DIA[dia.status]}
                </span>
              </li>
            ))}
          </ul>

          {execucao.estado === "finalizado" && (
            <ResumoFinal execucao={execucao} />
          )}
          {execucao.sessaoExpirada && <InstrucoesSessaoExpirada />}

          <div className="mt-5 flex justify-end gap-2">
            {executando ? (
              <Button
                variant="secondary"
                onClick={onInterromper}
                disabled={execucao.interrompendo}
              >
                {execucao.interrompendo
                  ? "Interrompendo após o dia atual…"
                  : "Interromper"}
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={onNovaIntegracao}>
                  Nova integração
                </Button>
                <Button onClick={onClose}>Fechar</Button>
              </>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default IntegracaoSisodontoModal;
